/**
 * Resume-on-reload persistence: {interviewId, token, exp} per survey in
 * localStorage. The stored token only shortcuts re-auth of the same
 * interview — the server re-verifies everything on every call.
 */
import { isClosedReason } from "./done";
import type { EmbedSession } from "./types";

const PREFIX = "deutero:iv:";

export interface StoredSession {
  interviewId: string;
  token: string;
  exp: number;
}

function storageKey(surveyId: string): string {
  return `${PREFIX}${surveyId}`;
}

export function loadSession(surveyId: string): StoredSession | null {
  try {
    const raw = localStorage.getItem(storageKey(surveyId));
    if (!raw) return null;
    const parsed = JSON.parse(raw) as StoredSession;
    if (!parsed.interviewId || !parsed.token) return null;
    return parsed;
  } catch {
    return null; // private mode / disabled storage / corrupt entry
  }
}

export function saveSession(surveyId: string, session: EmbedSession): void {
  try {
    localStorage.setItem(
      storageKey(surveyId),
      JSON.stringify({
        interviewId: session.interview_id,
        token: session.session_token,
        exp: session.expires_at,
      } satisfies StoredSession)
    );
  } catch {
    /* storage unavailable — resume simply won't work */
  }
}

export function clearSession(surveyId: string): void {
  try {
    localStorage.removeItem(storageKey(surveyId));
  } catch {
    /* ignore */
  }
}

/* ------------------------------------------------- completion record (local)

   Scoped per survey, alongside the in-flight session above. Without it a
   reload after completion starts a *new* interview, which for a full study
   comes straight back as "maximum number of responses" — the visitor who just
   finished is told the study is closed. Only *completions* are recorded: a
   refusal must not be remembered, or a study that reopens (capacity raised,
   billing fixed) stays closed forever for everyone who saw the denial once. */

export interface StoredCompletion {
  reason: string;
  at: number;
}

function doneKey(surveyId: string): string {
  return `${PREFIX}done:${surveyId}`;
}

export function persistCompletion(surveyId: string, reason = ""): void {
  if (!surveyId || isClosedReason(reason)) return;
  try {
    localStorage.setItem(
      doneKey(surveyId),
      JSON.stringify({ reason, at: Date.now() } satisfies StoredCompletion)
    );
  } catch {
    /* storage unavailable — a reload just starts a new interview */
  }
}

/** The recorded completion for this survey, or null. */
export function loadCompletion(surveyId: string): StoredCompletion | null {
  if (!surveyId) return null;
  try {
    const raw = localStorage.getItem(doneKey(surveyId));
    if (!raw) return null;
    const rec = JSON.parse(raw) as StoredCompletion;
    return rec && typeof rec === "object" ? rec : null;
  } catch {
    return null;
  }
}

export function clearCompletion(surveyId: string): void {
  try {
    localStorage.removeItem(doneKey(surveyId));
  } catch {
    /* ignore */
  }
}
